import React, { useState, useEffect, useCallback } from 'react';
import {
  Row, Col, Card, Select, Typography, Table, Tag, Space, Button, Avatar,
  Progress, Statistic, Empty, Badge, Tooltip,
} from 'antd';
import {
  TrophyOutlined, ReloadOutlined, FireOutlined, RiseOutlined, ClockCircleOutlined,
  EnvironmentOutlined, RightOutlined, CrownOutlined, StarOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Cell, Tooltip as RTooltip, CartesianGrid } from 'recharts';
import { getEvents, getEngagement } from '../api';

const { Title, Text } = Typography;
const { Option } = Select;

const PRIMARY = '#0F1D6B';
const TEAL    = '#00838F';

const BADGES = {
  platinum: { color: '#e5e4e2', text: '#475569', label: 'PLATINUM', icon: <CrownOutlined /> },
  gold:     { color: '#fbbf24', text: '#92400e', label: 'GOLD',     icon: <TrophyOutlined /> },
  silver:   { color: '#cbd5e1', text: '#475569', label: 'SILVER',   icon: <StarOutlined /> },
  bronze:   { color: '#cd7f32', text: '#fff',    label: 'BRONZE',   icon: <StarOutlined /> },
  rookie:   { color: '#e5e7eb', text: '#6b7280', label: 'ROOKIE',   icon: null },
};

const fmtMins = (m) => {
  if (!m || m < 1) return '0m';
  if (m < 60) return `${Math.round(m)}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${Math.round(m % 60)}m`;
};

export default function EngagementLeaderboard() {
  const navigate = useNavigate();
  const [events, setEvents]   = useState([]);
  const [eventId, setEventId] = useState(null);
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getEvents().then(r => {
      const ev = r.data.data || [];
      setEvents(ev);
      const a = ev.find(e => e.status === 'active') || ev[0];
      if (a) setEventId(a.id);
    }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    if (!eventId) return;
    setLoading(true);
    getEngagement({ event_id: eventId, limit: 100 })
      .then(r => setData(r.data.data || []))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  const top3 = data.slice(0, 3);
  const rest = data.slice(3);
  const distribution = ['platinum', 'gold', 'silver', 'bronze', 'rookie'].map(b => ({
    badge: b.toUpperCase(),
    count: data.filter(d => d.badge === b).length,
    color: BADGES[b].color,
  }));

  const PodiumCard = ({ entry, rank }) => {
    if (!entry) return null;
    const b = BADGES[entry.badge] || BADGES.rookie;
    const sizes = { 1: { h: 220, fs: 64, av: 90 }, 2: { h: 180, fs: 48, av: 70 }, 3: { h: 150, fs: 38, av: 60 } };
    const sz = sizes[rank];
    const colors = { 1: '#fbbf24', 2: '#cbd5e1', 3: '#cd7f32' };
    return (
      <div style={{
        background: `linear-gradient(180deg, ${colors[rank]} 0%, ${colors[rank]}dd 100%)`,
        borderRadius: 12, padding: 16, height: sz.h, position: 'relative',
        cursor: 'pointer', boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
      }}
      onClick={() => navigate(`/delegate-journey/${entry.delegate_id}?event_id=${eventId}`)}>
        <div style={{
          position: 'absolute', top: 8, right: 12,
          fontSize: sz.fs, fontWeight: 900, color: 'rgba(255,255,255,0.5)', lineHeight: 1,
        }}>#{rank}</div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', height: '100%' }}>
          <Avatar size={sz.av} style={{ background: '#fff', color: PRIMARY, fontWeight: 900, fontSize: sz.av * 0.36 }}>
            {(entry.delegate_name || '?').charAt(0).toUpperCase()}
          </Avatar>
          <Text strong style={{ color: '#fff', fontSize: rank === 1 ? 16 : 13, marginTop: 8, lineHeight: 1.2 }}>
            {entry.delegate_name}
          </Text>
          {entry.category && (
            <Tag color={entry.category_color} style={{ marginTop: 4, fontSize: 10 }}>{entry.category}</Tag>
          )}
          <div style={{
            marginTop: 'auto', background: 'rgba(0,0,0,0.25)', borderRadius: 8,
            padding: '6px 14px', color: '#fff', fontSize: rank === 1 ? 22 : 18, fontWeight: 800,
          }}>
            {entry.engagement_score} <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 10 }}>/ 100</Text>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <Row align="middle" justify="space-between" style={{ marginBottom: 20 }}>
        <Col>
          <Title level={4} style={{ margin: 0, color: PRIMARY }}>
            <TrophyOutlined style={{ marginRight: 8, color: '#fbbf24' }} />Engagement Leaderboard
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Gamified ranking of delegates by overall event engagement
          </Text>
        </Col>
        <Col>
          <Space>
            <Select value={eventId} onChange={setEventId} style={{ width: 240 }} placeholder="Select Event">
              {events.map(e => (
                <Option key={e.id} value={e.id}>
                  <Badge color={e.status === 'active' ? 'green' : 'default'} text={e.event_name} />
                </Option>
              ))}
            </Select>
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
          </Space>
        </Col>
      </Row>

      {data.length === 0 && !loading ? (
        <Empty description="No engagement data yet — delegates need to start scanning" />
      ) : (
        <>
          {/* Podium Top 3 */}
          <Card style={{
            borderRadius: 12, marginBottom: 16,
            background: 'linear-gradient(135deg, #1e293b 0%, #0F1D6B 100%)',
          }}>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11, letterSpacing: 4, textTransform: 'uppercase' }}>
                ✨ Top Performers ✨
              </Text>
            </div>
            <Row gutter={16} align="bottom" justify="center">
              <Col xs={24} sm={8}><PodiumCard entry={top3[1]} rank={2} /></Col>
              <Col xs={24} sm={8}><PodiumCard entry={top3[0]} rank={1} /></Col>
              <Col xs={24} sm={8}><PodiumCard entry={top3[2]} rank={3} /></Col>
            </Row>
          </Card>

          {/* Badge distribution */}
          <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
            {distribution.map(d => (
              <Col xs={12} md={4} lg={4} xl={4} key={d.badge}>
                <Card size="small" style={{ borderRadius: 10, borderTop: `3px solid ${d.color}` }}>
                  <Statistic title={d.badge} value={d.count}
                    valueStyle={{ color: d.color === '#e5e7eb' ? '#94a3b8' : d.color, fontWeight: 700 }} />
                </Card>
              </Col>
            ))}
          </Row>

          {/* Full leaderboard table */}
          <Card title={<Space><TrophyOutlined style={{ color: PRIMARY }} /> Full Leaderboard ({data.length})</Space>}
            style={{ borderRadius: 10 }}>
            <Table
              dataSource={data}
              rowKey="delegate_id"
              size="small"
              pagination={{ pageSize: 25, showSizeChanger: true }}
              onRow={(r) => ({
                onClick: () => navigate(`/delegate-journey/${r.delegate_id}?event_id=${eventId}`),
                style: { cursor: 'pointer' },
              })}
              columns={[
                {
                  title: '#', width: 50, align: 'center',
                  render: (_, __, i) => {
                    const rank = i + 1;
                    if (rank <= 3) {
                      const colors = ['#fbbf24', '#cbd5e1', '#cd7f32'];
                      return <Avatar size={28} style={{ background: colors[rank-1], color: '#fff', fontWeight: 700 }}>{rank}</Avatar>;
                    }
                    return <Text strong style={{ color: '#94a3b8' }}>{rank}</Text>;
                  }
                },
                {
                  title: 'Delegate', dataIndex: 'delegate_name',
                  render: (v, r) => (
                    <Space>
                      <Avatar size={32} style={{ background: r.category_color || PRIMARY }}>
                        {(v || '?').charAt(0).toUpperCase()}
                      </Avatar>
                      <Space direction="vertical" size={0}>
                        <Text strong>{v}</Text>
                        <Text type="secondary" style={{ fontSize: 10 }}>{r.company || '-'}</Text>
                      </Space>
                    </Space>
                  )
                },
                {
                  title: 'Category', dataIndex: 'category', width: 110,
                  render: (v, r) => v ? <Tag color={r.category_color}>{v}</Tag> : '-'
                },
                {
                  title: 'Badge', dataIndex: 'badge', width: 110, align: 'center',
                  render: (v) => {
                    const b = BADGES[v] || BADGES.rookie;
                    return (
                      <Tag style={{ background: b.color, color: b.text, border: 'none', fontWeight: 700, padding: '2px 10px' }}>
                        {b.icon && <span style={{ marginRight: 4 }}>{b.icon}</span>}{b.label}
                      </Tag>
                    );
                  }
                },
                {
                  title: 'Score', dataIndex: 'engagement_score', width: 160, align: 'center',
                  render: (v) => (
                    <Tooltip title={`Score breakdown: zones (30%) + time (25%) + scans (25%) + movements (20%)`}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Progress percent={v} size="small" strokeColor={
                          v >= 80 ? '#fbbf24' : v >= 60 ? '#fbbf24' : v >= 40 ? '#94a3b8' : '#cd7f32'
                        } showInfo={false} style={{ flex: 1 }} />
                        <Text strong style={{ minWidth: 32, color: PRIMARY }}>{v}</Text>
                      </div>
                    </Tooltip>
                  ),
                  sorter: (a, b) => a.engagement_score - b.engagement_score,
                  defaultSortOrder: 'descend',
                },
                { title: 'Zones', dataIndex: 'unique_zones', width: 70, align: 'center',
                  render: v => <Tag color="cyan">{v}</Tag> },
                { title: 'Scans', dataIndex: 'total_scans', width: 80, align: 'center' },
                { title: 'Time', dataIndex: 'time_at_event_minutes', width: 90, align: 'right',
                  render: v => <Text style={{ color: TEAL }}>{fmtMins(v)}</Text> },
                { title: 'Moves', dataIndex: 'movement_count', width: 70, align: 'center' },
                { title: '', width: 30, render: () => <RightOutlined style={{ color: '#bbb' }} /> },
              ]}
            />
          </Card>
        </>
      )}
    </div>
  );
}
